import { IServiceHook } from '@models';
import { GarageService } from '@services';
import { useStore } from '@stores';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

interface IProps {
  showMyServicesOnly?: boolean;
  serviceGroupId?: number;
}

export const useServices = ({
  showMyServicesOnly,
  serviceGroupId,
}: IProps): IServiceHook => {
  const { serviceStore } = useStore();
  const { services, totalCount, loading, skipCount, currentPage, pageSize, handlePageChange } =
    serviceStore;
  const [name, setName] = useState<string | null>('');
  const [code, setCode] = useState<string | null>('');
  const [groupName, setGroupName] = useState<string | null>('');
  const [isVat, setIsVat] = useState<boolean | undefined>(undefined);
  const [activity, setActivity] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    serviceStore.getServices({
      name,
      code,
      serviceGroupId,
      groupName,
      isVat,
      maxResultCount: pageSize,
      skipCount,
      showMyServicesOnly,
      isActive: activity,
    });

    return () => {
      serviceStore.clearServices();
    };
  }, [
    serviceStore,
    name,
    code,
    groupName,
    isVat,
    activity,
    serviceGroupId,
    showMyServicesOnly,
    pageSize,
    skipCount,
  ]);

  const handleNameSearch = debounce((text: string) => {
    setName(text);
    serviceStore.setSkipCount(0);
    serviceStore.setCurrentPage(1);
  }, 500);

  const handleCodeSearch = debounce((text: string) => {
    setCode(text);
    serviceStore.setSkipCount(0);
    serviceStore.setCurrentPage(1);
  }, 500);

  const handleGroupSearch = debounce((text: string) => {
    setGroupName(text);
    serviceStore.setSkipCount(0);
    serviceStore.setCurrentPage(1);
  }, 500);

  const handleIsVatChange = (value: boolean | undefined) => {
    setIsVat(value);
    serviceStore.setSkipCount(0);
    serviceStore.setCurrentPage(1);
  };

  const handleActivityChange = (value: boolean | undefined) => {
    setActivity(value);
    serviceStore.setSkipCount(0);
    serviceStore.setCurrentPage(1);
  };

  const onActiveChange = useCallback(
    async (id: number) => await serviceStore.changeActive(id),
    [serviceStore]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      await serviceStore.delete({ id });
    },
    [serviceStore]
  );

  const fetchAllForExport = useCallback(async () => {
    const result = await GarageService.getAll({
      name,
      code,
      serviceGroupId,
      groupName,
      isVat,
      isActive: activity,
      showMyServicesOnly,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [name, code, serviceGroupId, groupName, isVat, activity, showMyServicesOnly, totalCount]);

  return {
    loading,
    dataSource: Array.from(services.values()),
    totalCount,
    handleGroupSearch,
    handleNameSearch,
    handleCodeSearch,
    currentPage,
    maxResultCount: pageSize,
    handleIsVatChange,
    handleActivityChange,
    handlePageChange,
    onActiveChange,
    handleDelete,
    fetchAllForExport,
  };
};
